/**
 * WebSocket Service
 * 
 * Manages real-time communication with the backend using Socket.io client.
 * Handles connection lifecycle, event subscriptions, and automatic reconnection.
 */

import { io, Socket } from 'socket.io-client';

/**
 * WebSocket event types for type-safe event handling
 */
export enum WebSocketEvent {
  // Connection events
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  CONNECT_ERROR = 'connect_error',
  RECONNECT = 'reconnect',
  RECONNECT_ATTEMPT = 'reconnect_attempt',
  RECONNECT_ERROR = 'reconnect_error',
  RECONNECT_FAILED = 'reconnect_failed',

  // Sync events
  SYNC_STARTED = 'sync:started',
  SYNC_PROGRESS = 'sync:progress',
  SYNC_COMPLETED = 'sync:completed',
  SYNC_FAILED = 'sync:failed',

  // Notification events
  NOTIFICATION_NEW = 'notification:new',
  NOTIFICATION_READ = 'notification:read',

  // Workout events
  WORKOUT_ASSIGNED = 'workout:assigned',
  WORKOUT_UPDATED = 'workout:updated',
  WORKOUT_COMPLETED = 'workout:completed',

  // Trainer events
  TRAINER_REQUEST = 'trainer:request',
  TRAINER_APPROVED = 'trainer:approved',
  TRAINER_REVOKED = 'trainer:revoked',

  // Activity events
  ACTIVITY_CREATED = 'activity:created',
  ACTIVITY_UPDATED = 'activity:updated',
  ACTIVITY_DELETED = 'activity:deleted',
}

/**
 * Sync progress data structure
 */
export interface SyncProgressData {
  userId: string;
  totalActivities: number;
  processedActivities: number;
  currentActivity?: string;
  percentage: number;
  status: 'in_progress' | 'completed' | 'failed';
  error?: string;
}

/**
 * Notification data structure
 */
export interface NotificationData {
  id: string;
  type: 'workout' | 'achievement' | 'trainer' | 'system';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

/**
 * Workout event data structure
 */
export interface WorkoutEventData {
  workoutId: string;
  athleteId: string;
  trainerId?: string;
  action: 'assigned' | 'updated' | 'completed';
  timestamp: string;
}

/**
 * Event callback type
 */
type EventCallback<T = any> = (data: T) => void;

/**
 * WebSocket Service Class
 * 
 * Singleton service for managing WebSocket connections and events
 */
class WebSocketService {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private eventListeners: Map<string, Set<EventCallback>> = new Map();

  /**
   * Initialize WebSocket connection
   * 
   * @param url - WebSocket server URL (defaults to API base URL)
   * @param token - JWT authentication token
   */
  public connect(url?: string, token?: string): void {
    if (this.socket?.connected) {
      console.warn('[WebSocket] Already connected');
      return;
    }

    const wsUrl = url || import.meta.env.VITE_WS_URL || 'http://localhost:8000';

    this.socket = io(wsUrl, {
      auth: {
        token: token || this.getStoredToken(),
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
    });

    this.setupEventHandlers();
  }

  /**
   * Set up internal event handlers for connection lifecycle
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on(WebSocketEvent.CONNECT, () => {
      console.log('[WebSocket] Connected successfully');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit(WebSocketEvent.CONNECT, { connected: true });
    });

    this.socket.on(WebSocketEvent.DISCONNECT, (reason: string) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.isConnected = false;
      this.emit(WebSocketEvent.DISCONNECT, { reason });
    });

    this.socket.on(WebSocketEvent.CONNECT_ERROR, (error: Error) => {
      console.error('[WebSocket] Connection error:', error.message);
      this.emit(WebSocketEvent.CONNECT_ERROR, { error: error.message });
    });

    this.socket.on(WebSocketEvent.RECONNECT_ATTEMPT, (attemptNumber: number) => {
      console.log(`[WebSocket] Reconnection attempt ${attemptNumber}/${this.maxReconnectAttempts}`);
      this.reconnectAttempts = attemptNumber;
      this.emit(WebSocketEvent.RECONNECT_ATTEMPT, { attemptNumber });
    });

    this.socket.on(WebSocketEvent.RECONNECT, (attemptNumber: number) => {
      console.log('[WebSocket] Reconnected after', attemptNumber, 'attempts');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit(WebSocketEvent.RECONNECT, { attemptNumber });
    });

    this.socket.on(WebSocketEvent.RECONNECT_ERROR, (error: Error) => {
      console.error('[WebSocket] Reconnection error:', error.message);
      this.emit(WebSocketEvent.RECONNECT_ERROR, { error: error.message });
    });

    this.socket.on(WebSocketEvent.RECONNECT_FAILED, () => {
      console.error('[WebSocket] Reconnection failed after maximum attempts');
      this.isConnected = false;
      this.emit(WebSocketEvent.RECONNECT_FAILED, {});
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.socket) {
      console.log('[WebSocket] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.eventListeners.clear();
    }
  }

  /**
   * Subscribe to a WebSocket event
   * 
   * @param event - Event name to subscribe to
   * @param callback - Callback function to execute when event is received
   * @returns Unsubscribe function
   */
  public on<T = any>(event: WebSocketEvent | string, callback: EventCallback<T>): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }

    this.eventListeners.get(event)!.add(callback);

    // Set up socket listener if not already set
    if (this.socket && !this.socket.hasListeners(event)) {
      this.socket.on(event, (data: T) => {
        this.emit(event, data);
      });
    }

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from a WebSocket event
   * 
   * @param event - Event name to unsubscribe from
   * @param callback - Callback function to remove
   */
  public off(event: WebSocketEvent | string, callback: EventCallback): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.eventListeners.delete(event);
        this.socket?.off(event);
      }
    }
  }

  /**
   * Emit event to all registered listeners
   * 
   * @param event - Event name
   * @param data - Event data
   */
  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[WebSocket] Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Send data to server
   * 
   * @param event - Event name
   * @param data - Data to send
   */
  public send(event: string, data?: any): void {
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot send data: not connected');
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Check if WebSocket is currently connected
   */
  public getConnectionStatus(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  /**
   * Get current reconnection attempt count
   */
  public getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Update authentication token for existing connection
   * 
   * @param token - New JWT token
   */
  public updateToken(token: string): void {
    if (this.socket) {
      this.socket.auth = { token };
      if (this.socket.connected) {
        this.socket.disconnect().connect();
      }
    }
  }

  /**
   * Get stored authentication token from localStorage
   */
  private getStoredToken(): string | null {
    try {
      return localStorage.getItem('auth_token');
    } catch (error) {
      console.error('[WebSocket] Error retrieving token:', error);
      return null;
    }
  }

  /**
   * Subscribe to sync progress events
   * 
   * @param callback - Callback function for sync progress updates
   * @returns Unsubscribe function
   */
  public onSyncProgress(callback: EventCallback<SyncProgressData>): () => void {
    return this.on(WebSocketEvent.SYNC_PROGRESS, callback);
  }

  /**
   * Subscribe to new notification events
   * 
   * @param callback - Callback function for new notifications
   * @returns Unsubscribe function
   */
  public onNotification(callback: EventCallback<NotificationData>): () => void {
    return this.on(WebSocketEvent.NOTIFICATION_NEW, callback);
  }

  /**
   * Subscribe to workout assignment events
   * 
   * @param callback - Callback function for workout assignments
   * @returns Unsubscribe function
   */
  public onWorkoutAssigned(callback: EventCallback<WorkoutEventData>): () => void {
    return this.on(WebSocketEvent.WORKOUT_ASSIGNED, callback);
  }

  /**
   * Subscribe to activity creation events
   * 
   * @param callback - Callback function for new activities
   * @returns Unsubscribe function
   */
  public onActivityCreated(callback: EventCallback<any>): () => void {
    return this.on(WebSocketEvent.ACTIVITY_CREATED, callback);
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;